/** Map AIPAdvice.adlSource keys to form field names used in AipAnalysis.js */
export const ADL_UI_FIELD_TO_SOURCE_KEY = {
  adlBathing: "adlBathing",
  adlDressing: "adlDressing",
  adlEating: "adlEating",
  adlTransferring: "adlTransferring",
  adlToileting: "adlToileting",
  adlContinence: "adlContinence",
  iadlMealPreparation: "iadlMeals",
  iadlShopping: "iadlShopping",
  iadlHousekeepingLaundry: "iadlHousekeeping",
  iadlMedication: "iadlMedication",
  iadlTransportation: "iadlTransportation",
  iadlFinances: "iadlFinances",
};

export function isAdlUiFieldFromWellSky(data, uiFieldKey) {
  const sourceKey = ADL_UI_FIELD_TO_SOURCE_KEY[uiFieldKey];
  if (!sourceKey || !data?.adlSource) return false;
  return data.adlSource[sourceKey] === "wellsky";
}

export function countWellSkyAdlFields(data) {
  return Object.keys(ADL_UI_FIELD_TO_SOURCE_KEY).filter((key) =>
    isAdlUiFieldFromWellSky(data, key),
  ).length;
}

export function isCareTeamFromWellSkyVisits(data = {}) {
  const src = String(data.source || "").toLowerCase();
  if (src.includes("wellsky") && src.includes("care")) return true;
  if (src === "wellsky-care-team-metrics") return true;
  if (data.careTeamMetricsUpdatedAt) return true;
  if (data.careTeamMetricsRaw?.visitCounts) return true;
  return false;
}

/**
 * Summarize which AIP areas include WellSky-derived inputs (for banners + score cards).
 */
/** Aligns Care Team Assessment inputs with backend visit metrics (Care Count source). */
export function hydrateCareTeamModelFromWellSkyMetrics(source = {}, base = {}) {
  const raw = source.careTeamMetricsRaw;
  const hasMetrics =
    Boolean(source.careTeamMetricsUpdatedAt) ||
    Boolean(raw?.visitCounts) ||
    isCareTeamFromWellSkyVisits(source);

  if (!hasMetrics) return null;

  const RELIABILITY_WEEKS = 13;
  const visit90 = Number(raw?.visitCounts?.d90 ?? 0);
  const topCount = Number(raw?.caregiverBreakdown?.[0]?.count ?? 0);
  const relPct = Number(source.ctReliabilityPercent ?? 0);
  const weeksWithVisit = Math.round((relPct / 100) * RELIABILITY_WEEKS);

  return {
    daysPerWeek: Number(source.ctDaysPerWeek ?? base.daysPerWeek ?? 0),
    totalVisits: visit90,
    visitsBySamePerson: topCount,
    scheduledVisits: RELIABILITY_WEEKS,
    deliveredOnTime: weeksWithVisit,
    totalVisits90d: visit90,
    carePlanUpToDate:
      source.ctCareplanCurrent === true || base.carePlanUpToDate === true,
    familySatisfaction: Number(
      source.ctFamilyCommunication ??
        source.familySatisfaction ??
        base.familySatisfaction ??
        3,
    ),
  };
}

export function deriveAipWellSkyLineage(data = {}, { clientLinked = false } = {}) {
  const adlCount = countWellSkyAdlFields(data);
  const carePlanAdl = adlCount > 0;
  const careTeamVisits = isCareTeamFromWellSkyVisits(data);
  const envProvisionalDefault = data?.esSource === "default";
  const provisionalAssessment =
    String(data?.status || "").toLowerCase() === "auto-provisional";

  const parts = [];
  if (carePlanAdl) {
    parts.push(
      adlCount === 1
        ? "1 ADL/IADL item from WellSky care plan"
        : `${adlCount} ADL/IADL items from WellSky care plan`,
    );
  }
  if (careTeamVisits) {
    parts.push("Care Team metrics from WellSky visit history");
  }
  if (envProvisionalDefault && provisionalAssessment) {
    parts.push("Environmental Safety provisional default (not yet assessed)");
  }

  const anyWellSkyAip = carePlanAdl || careTeamVisits;
  const showBanner = clientLinked && (anyWellSkyAip || provisionalAssessment);

  return {
    clientLinked,
    carePlanAdl,
    adlCount,
    careTeamVisits,
    envProvisionalDefault,
    provisionalAssessment,
    anyWellSkyAip,
    showBanner,
    summaryParts: parts,
    summaryText: parts.length ? parts.join(" · ") : "",
  };
}
